import api from '../../../../api/client'
import { toJobOption, toTaskOption } from '../../common/pmOptionUtils'

function normalizeOptionsPayload(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  return []
}

function isBlankClientRp(value) {
  if (!value || typeof value !== 'object') return false
  const role = String(value.role || '').trim()
  const name = String(value.name || '').trim()
  const email = String(value.email || '').trim()
  const phone = String(value.phone || '').trim()
  return !role && !name && !email && !phone
}

function sanitizeJobPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload
  const next = { ...payload }
  if (isBlankClientRp(next.client_rp)) {
    delete next.client_rp
  }
  return next
}

export async function listJobs(params) {
  const { data } = await api.get('/jobs/', { params })
  return data
}

export async function getJobDetail(jobId) {
  const { data } = await api.get(`/jobs/${jobId}`)
  return data
}

export async function fetchJobPointsPreview(jobId) {
  const params = jobId ? { jobId: Number(jobId) } : {}
  const { data } = await api.get('/jobs/points-preview', { params })
  return data || {}
}

export async function createJob(payload) {
  const { data } = await api.post('/jobs/', sanitizeJobPayload(payload))
  return data
}

export async function updateJob(jobId, payload) {
  const { data } = await api.put(`/jobs/${jobId}`, sanitizeJobPayload(payload))
  return data
}

export async function uploadJobAttachment(jobId, file) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await api.post(`/jobs/${jobId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteJobAttachment(jobId, attachmentId) {
  const { data } = await api.delete(`/jobs/${jobId}/attachments/${attachmentId}`)
  return data
}

export async function getJobRemarks(jobId) {
  const { data } = await api.get(`/jobs/${jobId}/remarks`)
  return data
}

export async function addJobRemark(jobId, message) {
  const { data } = await api.post(`/jobs/${jobId}/remarks`, { message })
  return data
}

export async function archiveJob(jobId) {
  const { data } = await api.post(`/jobs/${jobId}/archive`)
  return data
}

export async function listJobTaskOptions(params) {
  const { data } = await api.get('/tasks/', { params })
  return normalizeOptionsPayload(data)
}

/**
 * Paginated task search for AppCombobox lazy loading.
 * Returns { items: [{ value, label }], hasMore: boolean }
 */
export async function fetchTaskOptionsPage(search, page, pageSize = 20) {
  const { data } = await api.get('/tasks/', {
    params: {
      page,
      page_size: pageSize,
      search: search || '',
      includeTotal: true,
      archive: false,
      fromDate: '1900-01-01',
      toDate: '9999-12-31',
    },
  })
  const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []
  const items = raw.map(toTaskOption).filter(Boolean)
  const total = Number(data?.total ?? 0)
  const hasMore = total > 0 ? page * pageSize < total : items.length === pageSize
  return { items, hasMore }
}

export async function fetchJobOptionsPage(search, page, pageSize = 20) {
  const { data } = await api.get('/jobs/', {
    params: {
      page,
      page_size: pageSize,
      search: search || '',
      includeTotal: true,
      archive: false,
      fromDate: '1900-01-01',
      toDate: '9999-12-31',
    },
  })
  const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []
  const items = raw.map(toJobOption).filter(Boolean)
  const total = Number(data?.total ?? 0)
  const hasMore = total > 0 ? page * pageSize < total : items.length === pageSize
  return { items, hasMore }
}

export async function listJobProjectOptions(params) {
  const { data } = await api.get('/projects/', { params })
  return normalizeOptionsPayload(data)
}

export async function listJobUserOptions(params) {
  const normalizedPageSize = Math.min(Number(params?.page_size || 50), 200)
  const normalizedParams = { ...(params || {}), page_size: normalizedPageSize }
  const { data } = await api.get('/options/users', { params: normalizedParams })
  return normalizeOptionsPayload(data)
}

export async function fetchJobUserOptionsPage(search, page, pageSize = 20, projectId = null) {
  const normalizedPageSize = Math.min(Number(pageSize || 20), 200)
  const params = {
    page,
    page_size: normalizedPageSize,
    search: search || '',
  }
  if (Number(projectId) > 0) {
    params.project_id = Number(projectId)
  }
  const { data } = await api.get('/options/users', {
    params,
  })
  const raw = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []
  const items = raw.map((user) => ({
    value: String(user.id),
    label: user.name,
    subLabel: user.email,
  }))
  const total = Number(data?.total ?? 0)
  const hasMore = total > 0 ? page * normalizedPageSize < total : items.length === normalizedPageSize
  return { items, hasMore }
}

function normalizeStakeholderRow(row) {
  if (!row || typeof row !== 'object') return null
  const role = String(row.role || row.contact_role || '').trim().toUpperCase()
  const name = String(row.name || row.contact_name || '').trim()
  const email = String(row.email || row.contact_email || '').trim().toLowerCase()
  const phoneNumbers = Array.isArray(row.phone_numbers)
    ? row.phone_numbers.map(p => String(p || '').trim())
    : Array.isArray(row.phoneNumbers)
    ? row.phoneNumbers.map(p => String(p || '').trim())
    : row.phone
    ? [String(row.phone || '').trim()]
    : []
  const phone = phoneNumbers.join(', ')
  if (!role && !name && !email && !phone) return null
  return { role, name, email, phone, phoneNumbers }
}

export async function getProjectStakeholdersByTask(taskId) {
  if (!taskId) return { projectId: null, projectName: '', stakeholders: [] }
  const { data: task } = await api.get(`/tasks/${taskId}`)
  const projectId = Number(task?.project_id || 0)
  if (!projectId) return { projectId: null, projectName: '', stakeholders: [] }
  const { data: project } = await api.get(`/projects/${projectId}`)
  const rawStakeholders = Array.isArray(project?.client_side_stakeholders) ? project.client_side_stakeholders : []
  const stakeholders = rawStakeholders.map(normalizeStakeholderRow).filter(Boolean)
  return {
    projectId,
    projectName: project?.name || '',
    stakeholders,
  }
}

export async function listTimesheetsByJob(jobId, params = {}) {
  if (!jobId) return []
  const { data } = await api.get('/timesheets/', {
    params: {
      page: 1,
      page_size: 100,
      includeTotal: false,
      archive: false,
      fromDate: '1900-01-01',
      toDate: '9999-12-31',
      job_id: jobId,
      approval_status: 'APPROVED',
      ...params,
    },
  })
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  return []
}

export async function validateJobCompletion(jobId) {
  const { data } = await api.get(`/jobs/${jobId}/validate-completion`)
  return data
}

export async function updateJobStatus(jobId, status, completionNote = null) {
  const { data } = await api.patch(`/jobs/${jobId}/status`, {
    status,
    completion_note: completionNote,
  })
  return data
}

